import * as React from "react";
import {
    ActionButton,
    Badge,
    Button,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
} from "@jhool-io/fe-components";
import { Link, useNavigate } from "react-router-dom";
import ListState from "../../../../components/ListState/ListState";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { ClientStatus, IClient } from "../../../../utils/types/client";
import {
    cn,
    displayNameInRightFormat,
    truncateString,
} from "../../../../utils/helpers";
import { useAppSelector } from "../../../../hooks/useRedux";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import Loader from "../../../../shared-ui/Loader/Loader";
import ArrowUpRightIcon from "../../../../icons/ArrowUpRight";

interface ClientSearchHistory {
    client_name: string;
    client_id: string;
    client_status?: ClientStatus;
}

function getClientStatusBadgeType(status: ClientStatus) {
    switch (status) {
        case ClientStatus.ACTIVE:
            return "success";
        case ClientStatus.INACTIVE:
            return "danger";
        case ClientStatus.PAUSED:
            return "warning";
        case ClientStatus.COMPLETED:
            return "info";
        default:
            return "neutral";
    }
}

export default function SearchClients() {
    const [showPopover, setShowPopover] = React.useState(false);
    const [searchInputValue, setSearchInputValue] = React.useState("");

    const { practice } = useAppSelector((state) => state.userPractice);

    const navigate = useNavigate();

    const debouncedSearchInputValue = useDebounce(searchInputValue);

    const { data, isLoading, error, isSuccess } = useFetchClientList(
        {
            search_string: debouncedSearchInputValue,
            limit: 20,
            provider_id: practice?.provider_id || "",
        },
        Boolean(searchInputValue.length > 2)
    );

    const clientsSearchHistoryLS = localStorage.getItem(
        `clients_search_history_${practice?.practice_id}`
    );

    const parsedClientSearchHistory: ClientSearchHistory[] =
        clientsSearchHistoryLS ? JSON.parse(clientsSearchHistoryLS) : [];

    const [searchHistory, setSearchHistory] = React.useState(
        () => parsedClientSearchHistory
    );

    const addToSearchHistory = (client: IClient) => {
        const newEntry: ClientSearchHistory = {
            client_name: `${client.first_name} ${client.last_name}`,
            client_id: client.client_id,
            client_status: client.client_status,
        };

        // Remove client if it is already present in the array
        const filteredParsedClientSearchHistory = searchHistory.filter(
            (cl) => cl.client_id !== newEntry.client_id
        );

        const newHistoryArray = [
            newEntry,
            ...filteredParsedClientSearchHistory,
        ].slice(0, 5);

        localStorage.setItem(
            `clients_search_history_${practice?.practice_id}`,
            JSON.stringify(newHistoryArray)
        );
        setSearchHistory(newHistoryArray);
    };

    const handleClientClick = (client: IClient) => {
        addToSearchHistory(client);
        setShowPopover(false);
        navigate(`/clients/${client.client_id}`);
    };

    const handleRemoveClientFromHistory = (clientId: string) => {
        const updatedList = searchHistory.filter(
            (cl) => cl.client_id !== clientId
        );

        localStorage.setItem(
            `clients_search_history_${practice?.practice_id}`,
            JSON.stringify(updatedList)
        );
        setSearchHistory(updatedList);
    };

    const isScreen1200Above = useMediaQuery("(min-width: 1200px)");

    return (
        <Popover open={showPopover} onOpenChange={setShowPopover}>
            <PopoverTrigger asChild>
                {isScreen1200Above ? (
                    <ActionButton
                        size="auto"
                        variant="normal"
                        className="![border:1px_solid_var(--color-stroke)] h-7 px-6 justify-start"
                        onClick={() => setShowPopover(true)}
                        aria-label="search-btn"
                        actionTitle="Search for client"
                        iconLeft={
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="size-3 shrink-0 stroke-textSec"
                            >
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        }
                    >
                        <span className="text-textSec text-base font-normal">
                            Search by client name, chart number
                        </span>
                    </ActionButton>
                ) : (
                    <ActionButton
                        size="auto"
                        variant="can-hover"
                        className="![border:1px_solid_var(--color-stroke)]"
                        onClick={() => setShowPopover(true)}
                        aria-label="search-btn"
                        actionTitle="Search for client"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="size-3 shrink-0 stroke-textSec"
                        >
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                    </ActionButton>
                )}
            </PopoverTrigger>
            <PopoverContent
                className={cn(
                    "w-[100vw] min-[400px]:w-[400px] max-w-full -mt-8 data-[state=open]:animate-[SlideTopIn_0.2s_ease-out] data-[state=closed]:animate-[SlideTopIn_0.2s_ease-out] max-h-80 overflow-y-auto",
                    {
                        "popover-full": isScreen1200Above,
                    }
                )}
                align={isScreen1200Above ? "start" : "center"}
            >
                <SearchInput
                    placeholder="Search..."
                    onChange={(e) => setSearchInputValue(e.target.value)}
                    value={searchInputValue}
                    inputSize="small"
                />
                <div className="py-2">
                    {searchInputValue.length > 2 && (
                        <div>
                            {isLoading ? (
                                <Loader wrapperClassName="h-[150px]" />
                            ) : null}
                            {error ? (
                                <ListState
                                    isError
                                    errorMsg="Error fetching clients"
                                    stateHelperText="Please try again later"
                                    context="general"
                                    cardClass="h-[150px]"
                                />
                            ) : null}
                            {data?.data && data.data.length === 0 ? (
                                <ListState
                                    isError={false}
                                    emptyMessage="No client(s) found"
                                    stateHelperText="Please enter search word"
                                    context="general"
                                    cardClass="h-[150px]"
                                />
                            ) : null}
                            {data?.data && isSuccess && (
                                <div className="flex flex-col gap-y-1">
                                    {data.data.map((client) => (
                                        <div
                                            key={client.client_id}
                                            className="flex items-start gap-x-2 justify-between text-caption"
                                        >
                                            <Button
                                                variant="can-hover"
                                                size="auto"
                                                className="min-w-0 flex-1 px-1.5 py-1 h-auto"
                                                onClick={() =>
                                                    handleClientClick(client)
                                                }
                                            >
                                                <div className="flex gap-1 text-left min-w-0 w-full flex-col min-[400px]:flex-row items-start min-[400px]:items-center">
                                                    <span className="font-medium capitalize whitespace-normal break-words leading-5 text-caption text-balance">
                                                        {truncateString(
                                                            displayNameInRightFormat(
                                                                {
                                                                    firstName:
                                                                        client.first_name,
                                                                    lastName:
                                                                        client.last_name,
                                                                }
                                                            ),
                                                            32
                                                        )}
                                                    </span>
                                                    {client.client_status && (
                                                        <Badge
                                                            type={getClientStatusBadgeType(
                                                                client.client_status
                                                            )}
                                                            content={
                                                                client.client_status
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </Button>
                                            <Button
                                                size="auto"
                                                variant="can-hover"
                                                className="px-1.5 py-1 h-auto shrink-0"
                                                aria-label={`Open ${displayNameInRightFormat(
                                                    {
                                                        firstName:
                                                            client.first_name,
                                                        lastName:
                                                            client.last_name,
                                                    }
                                                )} profile in new tab`}
                                                title={`Open ${displayNameInRightFormat(
                                                    {
                                                        firstName:
                                                            client.first_name,
                                                        lastName:
                                                            client.last_name,
                                                    }
                                                )} profile in new tab`}
                                                onClick={() => {
                                                    addToSearchHistory(client);
                                                    window.open(
                                                        `/clients/${client.client_id}`,
                                                        "_blank"
                                                    );
                                                }}
                                            >
                                                <ArrowUpRightIcon />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {searchInputValue.length <= 2 && (
                        <div>
                            {searchHistory.length > 0 ? (
                                <div>
                                    <span className="font-medium uppercase text-caption my-1 block pl-6">
                                        Recent searches
                                    </span>
                                    <div className="flex flex-col gap-y-1">
                                        {searchHistory.map((client) => (
                                            <div
                                                key={client.client_id}
                                                className="flex items-start gap-x-2 justify-between text-caption"
                                            >
                                                <Link
                                                    to={`/clients/${client.client_id}`}
                                                    className="link flex-1 min-w-0 block px-1.5 py-1 h-auto"
                                                >
                                                    <div className="fflex gap-1 text-left min-w-0 w-full flex-col min-[400px]:flex-row items-start min-[400px]:items-center">
                                                        <span className="font-medium capitalize whitespace-normal break-words leading-5 text-balance">
                                                            {truncateString(
                                                                client.client_name,
                                                                32
                                                            )}
                                                        </span>
                                                        {client.client_status && (
                                                            <Badge
                                                                type={getClientStatusBadgeType(
                                                                    client.client_status
                                                                )}
                                                                content={
                                                                    client.client_status
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </Link>
                                                <div className="flex items-center gap-x-1">
                                                    <Button
                                                        size="auto"
                                                        variant="can-hover"
                                                        className="px-1.5 py-1 h-auto shrink-0"
                                                        aria-label={`Open ${client.client_name} profile in new tab`}
                                                        title={`Open ${client.client_name} profile in new tab`}
                                                        onClick={() =>
                                                            window.open(
                                                                `/clients/${client.client_id}`,
                                                                "_blank"
                                                            )
                                                        }
                                                    >
                                                        <ArrowUpRightIcon />
                                                    </Button>
                                                    <Button
                                                        size="auto"
                                                        variant="can-hover"
                                                        className="px-1.5 py-1 h-auto shrink-0"
                                                        onClick={() =>
                                                            handleRemoveClientFromHistory(
                                                                client.client_id
                                                            )
                                                        }
                                                        aria-label={`Remove ${client.client_name} from recent searches`}
                                                        title={`Remove ${client.client_name} from recent searches`}
                                                    >
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            width="24"
                                                            height="24"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            fill="none"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="size-3.5 shrink-0"
                                                        >
                                                            <line
                                                                x1="18"
                                                                y1="6"
                                                                x2="6"
                                                                y2="18"
                                                            />
                                                            <line
                                                                x1="6"
                                                                y1="6"
                                                                x2="18"
                                                                y2="18"
                                                            />
                                                        </svg>
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <ListState
                                    isError={false}
                                    context="general"
                                    emptyMessage="No recent search"
                                    stateHelperText="Your search history will appear here"
                                />
                            )}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
